import React, { useEffect, useState, useContext } from "react";
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Spinner } from "./Spinner";
import { showDefaultAlert } from '../utils/Alerts';
import { UserContext } from '../contexts/UserContext';
import AddDealerImportedVehicleModel from "./AddDealerImportedVehicleModel";
import { getDealerImportedVehicles } from '../services/DealerImportedVehicleAPI';

export default function DealerImportedVehicles() {
  const { user, token } = useContext(UserContext);
  const [loaded, setLoaded] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [importedVehicles, setImportedVehicles] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoaded(true);
      try {
        const res = await getDealerImportedVehicles(user.UserID, user.DealerID, token);
        setImportedVehicles(res.data);
      } catch (error) {
        console.log(error);
        showDefaultAlert('error', 'Error', 'Failed to retrieve the Imported Vehicle');
      } finally {
        setLoaded(false);
      }
    };
    fetchData();
  }, []);

  const interestCount = (interests) => {
    let count = 0;
    interests.forEach((element) => {
      if (element.Interest === true) {
        count++;
      }
    });
    return count;
  };

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  return (
    <>
      {loaded ? (
        <div className="middle">
          <Spinner></Spinner>
        </div>
      ) : (
        <>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={11} lg={10} sx={{ mt: { xs: 3, sm: 6 }, px: { xs: 2, sm: 0 } }}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid #c52b2b',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#ffffff'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    gap: 2,
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' },
                    backgroundColor: '#c52b2b',
                    color: '#ffffff'
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Imported Vehicles
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Track vehicles and customer interest
                    </Typography>
                  </Box>
                  <Button
                    className="btn"
                    type="submit"
                    variant="contained"
                    onClick={handleOpenAddModal}
                    sx={{
                      backgroundColor: '#ffffff',
                      color: '#c52b2b',
                      border: '1px solid #ffffff',
                      fontWeight: 700,
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': { backgroundColor: '#f7dede', borderColor: '#f7dede' }
                    }}
                  >
                    Add Imported Vehicle
                  </Button>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <AddDealerImportedVehicleModel open={isAddModalOpen} handleClose={handleCloseAddModal} addImportedVehicle={setImportedVehicles} />
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 1.5,
                      overflowX: 'auto'
                    }}
                  >
                    <Table size="small" aria-label="imported vehicles" sx={{ minWidth: 960 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#c52b2b' }}>
                          <TableCell sx={{ color: '#ffffff', fontWeight: 700 }}>Chassis No</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Model</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Year</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Engine</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Transmission</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Grade</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Color</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Status</TableCell>
                          <TableCell align="left" sx={{ color: '#ffffff', fontWeight: 700 }}>Interests</TableCell>
                        </TableRow>
                      </TableHead>
                      {importedVehicles.length === 0 ? (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ color: '#111111', py: 4 }}>
                              No imported vehicles found
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      ) : (
                        <TableBody>
                          {importedVehicles
                            .slice()
                            .sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt))
                            .map((importedVehicle) => (
                              <TableRow
                                key={importedVehicle.ChassisNo}
                                sx={{
                                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                  '&:last-child td, &:last-child th': { borderBottom: 0 }
                                }}
                              >
                                <TableCell component="th" scope="row" align="left" sx={{ color: '#111111' }}>
                                  {importedVehicle.ChassisNo}
                                </TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>{importedVehicle.Model}</TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>{importedVehicle.Year}</TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>{importedVehicle.Engine}</TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>{importedVehicle.Transmission}</TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>{importedVehicle.Grade}</TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>{importedVehicle.Color}</TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>{importedVehicle.Status}</TableCell>
                                <TableCell align="left" sx={{ color: '#111111' }}>
                                  {Array.isArray(importedVehicle.Interests)
                                    ? interestCount(importedVehicle.Interests)
                                    : 0}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
